import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/** Cross Co step: pick a guarantor → Companies House lists every company they're an officer of →
 *  tick → add as X/Company guarantees linked to THAT guarantor only (Dean: Business G of
 *  Director B must never print on Director A's deed). */
export function GuarantorCompaniesFinder({ guarantorNames, directors, borrowerCompanyNumber, crossCompanies, onAdd }) {
  const [who, setWho] = useState(guarantorNames[0] || "");
  const [loading, setLoading] = useState(false);
  const [officers, setOfficers] = useState(null);
  const [ticked, setTicked] = useState({});
  const [adding, setAdding] = useState(false);

  const already = new Set(crossCompanies.map((c) => c.company_number));
  const find = async () => {
    if (!who) return;
    setLoading(true); setOfficers(null); setTicked({});
    try {
      const dob = (directors.find((d) => d.name === who) || {}).date_of_birth || "";
      const r = (await api.get("/companies-house/officer-appointments", { params: { name: who, dob: typeof dob === "string" ? dob : "" } })).data;
      if (!r.success) { toast.error(r.error || "Companies House search failed"); return; }
      setOfficers(r.officers || []);
      const pre = {};
      (r.officers || []).forEach((o) => o.appointments.forEach((a) => {
        if (a.active && a.role === "director" && a.company_number !== borrowerCompanyNumber && !already.has(a.company_number)) pre[a.company_number] = true;
      }));
      setTicked(pre);
      if (!(r.officers || []).length) toast.info(`No Companies House officer records found for "${who}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Companies House lookup failed"); } finally { setLoading(false); }
  };

  const add = async () => {
    const nums = Object.keys(ticked).filter((k) => ticked[k]);
    if (!nums.length) { toast.error("Tick at least one company"); return; }
    if (crossCompanies.length + nums.length > 6) { toast.error(`Maximum 6 cross-company guarantees (${crossCompanies.length} already added)`); return; }
    setAdding(true);
    try {
      const built = [];
      for (const n of nums) {
        const [p, o] = await Promise.all([api.get(`/companies-house/company/${n}`), api.get(`/companies-house/company/${n}/officers`)]);
        const prof = p.data; const addr = prof.registered_office_address || {};
        const active = (o.data.items || []).filter((x) => x.officer_role === "director" && !x.resigned_on);
        built.push({
          company_number: n, company_name: prof.company_name,
          registered_address: [addr.address_line_1, addr.locality, addr.postal_code].filter(Boolean).join(", "),
          signing_director_name: who, officer_names: active.map((d) => d.name),
        });
      }
      onAdd(who, built);
      toast.success(`Added ${built.length} company guarantee(s) to ${who}'s deed`);
      setOfficers(null); setTicked({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add companies"); } finally { setAdding(false); }
  };

  return (
    <Card className="border-indigo-200 mb-4" data-testid="guarantor-companies-finder">
      <CardContent className="p-3 space-y-3">
        <div>
          <p className="text-sm font-medium flex items-center gap-2"><Building2 className="w-4 h-4 text-indigo-700" />Find a guarantor's companies on Companies House</p>
          <p className="text-xs text-muted-foreground">Lists every company the guarantor is an officer of. Ticked companies are added as cross-company guarantees on <b>their</b> deed only — other guarantors' deeds are untouched.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[16rem]">
            <Label className="text-xs">Guarantor</Label>
            <Select value={who} onValueChange={setWho}>
              <SelectTrigger className="h-9" data-testid="finder-guarantor-select"><SelectValue placeholder="Choose guarantor" /></SelectTrigger>
              <SelectContent>{guarantorNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={find} disabled={!who || loading} data-testid="finder-search-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Find their companies
          </Button>
        </div>
        {officers && officers.length > 0 && (
          <div className="space-y-2" data-testid="finder-results">
            {officers.map((o, i) => (
              <div key={`${o.officer_id}-${i}`} className="rounded-md border p-2">
                <div className="text-xs text-muted-foreground mb-1">{o.title}{o.dob ? ` · born ${o.dob}` : ""}{o.address ? ` · ${o.address}` : ""}</div>
                {o.appointments.length === 0 && <div className="text-xs italic text-muted-foreground">No appointments</div>}
                {o.appointments.map((a) => {
                  const isBorrower = a.company_number === borrowerCompanyNumber; const dup = already.has(a.company_number);
                  return (
                    <label key={`${a.company_number}-${a.role}`} className={`flex items-center gap-2 py-0.5 text-sm ${(!a.active || isBorrower || dup) ? "text-muted-foreground" : ""}`}>
                      <Checkbox checked={!!ticked[a.company_number]} disabled={isBorrower || dup} onCheckedChange={(v) => setTicked((p) => ({ ...p, [a.company_number]: !!v }))}
                        data-testid={`finder-company-${a.company_number}`} />
                      <span className="font-medium">{a.company_name}</span>
                      <span className="text-xs">{a.company_number} · {a.role}{a.resigned_on ? ` · resigned ${a.resigned_on}` : ""}{a.company_status && a.company_status !== "active" ? ` · ${a.company_status}` : ""}</span>
                      {isBorrower && <span className="text-[11px] text-emerald-700">borrower</span>}
                      {dup && <span className="text-[11px] text-indigo-700">already added</span>}
                    </label>
                  );
                })}
              </div>
            ))}
            <Button onClick={add} disabled={adding || !Object.values(ticked).some(Boolean)} data-testid="finder-add-btn">
              {adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Add ticked companies to {who}'s guarantee
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
